import { Box, Link, Typography } from "@mui/material";
import Markdown, { type Components } from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import remarkGfm from "remark-gfm";

function getInternalLinkTarget(href: string | undefined) {
  if (!href) {
    return undefined;
  }

  if (href.startsWith("/") && !href.startsWith("//")) {
    return href;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const url = new URL(href);
    if (url.origin !== window.location.origin) {
      return undefined;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mt: 0, mb: 1.25, lineHeight: 1.55 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => {
    const internalLinkTarget = getInternalLinkTarget(href);
    if (internalLinkTarget) {
      return (
        <Link component={RouterLink} to={internalLinkTarget} underline="always">
          {children}
        </Link>
      );
    }

    const isExternal = href?.startsWith("http://") || href?.startsWith("https://");
    return (
      <Link
        href={href ?? undefined}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        underline="always"
      >
        {children}
      </Link>
    );
  },
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.25 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 1.25 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.25 }}>
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700 }}>
      {children}
    </Box>
  ),
};

export function NotificationMarkdown({ bodyMarkdown }: { bodyMarkdown: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {bodyMarkdown}
    </Markdown>
  );
}
